import { useState, useEffect } from 'react';
import { Mail, Check, X, Plug, CheckCircle, AlertCircle, Plus, Save, RotateCcw, Server, Settings, Bell, Eye, Shield } from 'lucide-react';

const EmailSettings = ({ settings, onSave, onChange }) => {
  const [formData, setFormData] = useState({
    smtpHost: settings?.smtpHost || '',
    smtpPort: settings?.smtpPort || 587,
    smtpUsername: settings?.smtpUsername || '',
    smtpPassword: settings?.smtpPassword || '',
    smtpEncryption: settings?.smtpEncryption || 'tls',
    fromEmail: settings?.fromEmail || '',
    fromName: settings?.fromName || '',
    replyToEmail: settings?.replyToEmail || '',
    emailSignature: settings?.emailSignature || '',
    autoSendInvoices: settings?.autoSendInvoices || false,
    autoSendQuotes: settings?.autoSendQuotes || false,
    autoSendReminders: settings?.autoSendReminders || false,
    reminderDaysBefore: settings?.reminderDaysBefore || 7,
    reminderDaysAfter: settings?.reminderDaysAfter || [1, 7, 14]
  });

  const [activeTab, setActiveTab] = useState('smtp');
  const [showPreview, setShowPreview] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        smtpHost: settings.smtpHost || '',
        smtpPort: settings.smtpPort || 587,
        smtpUsername: settings.smtpUsername || '',
        smtpPassword: settings.smtpPassword || '',
        smtpEncryption: settings.smtpEncryption || 'tls',
        fromEmail: settings.fromEmail || '',
        fromName: settings.fromName || '',
        replyToEmail: settings.replyToEmail || '',
        emailSignature: settings.emailSignature || '',
        autoSendInvoices: settings.autoSendInvoices || false,
        autoSendQuotes: settings.autoSendQuotes || false,
        autoSendReminders: settings.autoSendReminders || false,
        reminderDaysBefore: settings.reminderDaysBefore || 7,
        reminderDaysAfter: settings.reminderDaysAfter || [1, 7, 14]
      });
    }
  }, [settings]);

  useEffect(() => {
    if (onChange) {
      onChange();
    }
  }, [formData, onChange]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value);
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear test result when SMTP settings change
    if (['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword', 'smtpEncryption'].includes(name)) {
      setTestResult(null);
    }
  };

  const handleArrayChange = (name, index, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].map((item, i) => i === index ? parseInt(value) || 0 : item)
    }));
  };

  const addReminderDay = () => {
    setFormData(prev => ({
      ...prev,
      reminderDaysAfter: [...prev.reminderDaysAfter, 30]
    }));
  };

  const removeReminderDay = (index) => {
    setFormData(prev => ({
      ...prev,
      reminderDaysAfter: prev.reminderDaysAfter.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    const finalData = { ...settings, ...formData };
    onSave(finalData);
  };

  const testConnection = async () => {
    if (!formData.smtpHost) {
      setTestResult({ success: false, message: 'SMTP host is required for testing' });
      return;
    }

    setIsTestingConnection(true);
    setTestResult(null);

    try {
      // Simulate API call - replace with actual implementation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setTestResult({ 
        success: true, 
        message: 'SMTP connection successful! Test email sent.' 
      });
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.message || 'SMTP connection failed' 
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const tabs = [
    { id: 'smtp', label: 'SMTP Server', icon: Server },
    { id: 'identity', label: 'Email Identity', icon: Mail },
    { id: 'automation', label: 'Automation', icon: Settings },
    { id: 'reminders', label: 'Reminders', icon: Bell }
  ];

  const getSmtpPresets = () => [
    {
      name: 'Gmail',
      host: 'smtp.gmail.com',
      port: 587,
      encryption: 'tls',
      note: 'Use App Password instead of regular password'
    },
    {
      name: 'Outlook/Hotmail',
      host: 'smtp-mail.outlook.com',
      port: 587,
      encryption: 'tls',
      note: 'Works with Microsoft accounts'
    },
    {
      name: 'Yahoo Mail',
      host: 'smtp.mail.yahoo.com',
      port: 587,
      encryption: 'tls',
      note: 'Requires app password'
    }
  ];

  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      smtpHost: preset.host,
      smtpPort: preset.port,
      smtpEncryption: preset.encryption
    }));
    setTestResult(null);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'smtp':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">SMTP Server Configuration</h3>
              <p className="text-blue-700 text-sm mb-6">Configure your email server settings to send invoices and notifications</p>
              
              {/* Quick Presets */}
              <div className="bg-white border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Quick Setup</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {getSmtpPresets().map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => applyPreset(preset)}
                      className="p-3 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="font-medium text-gray-800">{preset.name}</div>
                      <div className="text-sm text-gray-600">{preset.host}</div>
                      <div className="text-xs text-gray-500 mt-1">{preset.note}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Manual Configuration */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-800 mb-4">Manual Configuration</h4>
                <div className="space-y-6">
                  {/* Host and Port */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SMTP Host <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        name="smtpHost"
                        value={formData.smtpHost}
                        onChange={handleChange}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Port <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        name="smtpPort"
                        value={formData.smtpPort}
                        onChange={handleChange}
                      />
                      <p className="text-xs text-gray-500 mt-1">Usually 587 or 465</p>
                    </div>
                  </div>

                  {/* Security */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Security <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full max-w-xs px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      name="smtpEncryption"
                      value={formData.smtpEncryption}
                      onChange={handleChange}
                    >
                      <option value="none">None (not recommended)</option>
                      <option value="tls">TLS (recommended)</option>
                      <option value="ssl">SSL</option>
                    </select>
                  </div>

                  {/* Credentials */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        name="smtpUsername"
                        value={formData.smtpUsername}
                        onChange={handleChange}
                        placeholder="your-email@domain.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                      <div className="relative">
                        <input
                          type="password"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pr-10"
                          name="smtpPassword"
                          value={formData.smtpPassword}
                          onChange={handleChange}
                          placeholder="••••••••••••"
                        />
                        <Shield size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">For Gmail, use an App Password</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Connection */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="flex flex-col space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-800">Test Configuration</h4>
                    <button
                      type="button"
                      className={`inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
                        testResult?.success 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : testResult?.success === false 
                            ? 'bg-red-100 text-red-700 border border-red-200' 
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                      } disabled:opacity-50`}
                      onClick={testConnection}
                      disabled={isTestingConnection || !formData.smtpHost}
                    >
                      {isTestingConnection ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Testing...
                        </>
                      ) : (
                        <>
                          {testResult?.success ? (
                            <CheckCircle size={16} className="mr-2" />
                          ) : testResult?.success === false ? (
                            <AlertCircle size={16} className="mr-2" />
                          ) : (
                            <Plug size={16} className="mr-2" />
                          )}
                          {testResult ? 'Test Again' : 'Test Connection'}
                        </>
                      )}
                    </button>
                  </div>
                  
                  {testResult && (
                    <div className={`p-4 rounded-lg border ${
                      testResult.success 
                        ? 'bg-green-50 border-green-200 text-green-700' 
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      <div className="flex items-center">
                        {testResult.success ? (
                          <CheckCircle size={20} className="mr-3 flex-shrink-0" />
                        ) : (
                          <AlertCircle size={20} className="mr-3 flex-shrink-0" />
                        )}
                        <div>
                          <div className="font-medium">
                            {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
                          </div>
                          <div className="text-sm">{testResult.message}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'identity':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Email Identity</h3>
              <p className="text-green-700 text-sm mb-6">Configure how your emails appear to customers</p>
              
              <div className="space-y-6">
                {/* From Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Sender Information</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        name="fromEmail"
                        value={formData.fromEmail}
                        onChange={handleChange}
                        placeholder="invoices@yourcompany.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">The email address invoices will be sent from</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        name="fromName"
                        value={formData.fromName}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                      />
                      <p className="text-xs text-gray-500 mt-1">The name that appears in the "From" field</p>
                    </div>
                  </div>
                </div>

                {/* Reply Settings */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Reply Settings</h4>
                  <div className="max-w-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reply To Email</label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      name="replyToEmail"
                      value={formData.replyToEmail}
                      onChange={handleChange}
                      placeholder="support@yourcompany.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">Where replies will be sent (optional)</p>
                  </div>
                </div>

                {/* Email Signature */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Email Signature</h4>
                  <div>
                    <textarea
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                      name="emailSignature"
                      value={formData.emailSignature}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Best regards,&#10;Your Company Name&#10;Phone: +1 (555) 123-4567&#10;Email: support@yourcompany.com&#10;Website: www.yourcompany.com"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-sm text-gray-500">This signature will be added to all outgoing emails</p>
                      <p className="text-xs text-gray-400">{formData.emailSignature.length}/1000 characters</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'automation':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Email Automation</h3>
              <p className="text-purple-700 text-sm mb-6">Automatically send documents when created or updated</p>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-800 mb-4">Auto-Send Settings</h4>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 mt-0.5"
                      type="checkbox"
                      name="autoSendInvoices"
                      checked={formData.autoSendInvoices}
                      onChange={handleChange}
                    />
                    <div className="ml-4">
                      <label className="text-base font-medium text-gray-900">
                        Auto-send invoices when created
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        Invoices will be automatically emailed to customers as soon as they're created
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 mt-0.5"
                      type="checkbox"
                      name="autoSendQuotes"
                      checked={formData.autoSendQuotes}
                      onChange={handleChange}
                    />
                    <div className="ml-4">
                      <label className="text-base font-medium text-gray-900">
                        Auto-send quotes when created
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        Quotes will be automatically emailed to customers as soon as they're created
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 mt-0.5"
                      type="checkbox"
                      name="autoSendReminders"
                      checked={formData.autoSendReminders}
                      onChange={handleChange}
                    />
                    <div className="ml-4">
                      <label className="text-base font-medium text-gray-900">
                        Auto-send payment reminders
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        Automatically send reminder emails for overdue invoices based on the schedule you set
                      </p>
                    </div>
                  </div>
                </div>

                {(formData.autoSendInvoices || formData.autoSendQuotes || formData.autoSendReminders) && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <span className="font-medium">⚠️ Important:</span> Make sure your SMTP settings are configured and tested before enabling automation. Failed emails may not be retried automatically.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'reminders':
        return (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">Payment Reminder Schedule</h3>
              <p className="text-orange-700 text-sm mb-6">Configure when to send payment reminders to customers</p>
              
              <div className="space-y-6">
                {/* Pre-due Reminders */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Before Due Date</h4>
                  <div className="max-w-sm">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Send reminder (days before due date)
                    </label>
                    <input
                      type="number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      name="reminderDaysBefore"
                      value={formData.reminderDaysBefore}
                      onChange={handleChange}
                      min="0"
                      max="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">Set to 0 to disable pre-due reminders</p>
                  </div>
                </div>

                {/* Post-due Reminders */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">After Due Date</h4>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">Send follow-up reminders on these days after the due date:</p>
                    
                    <div className="space-y-3">
                      {formData.reminderDaysAfter.map((days, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <input
                            type="number"
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            value={days}
                            onChange={(e) => handleArrayChange('reminderDaysAfter', index, e.target.value)}
                            min="1"
                            max="365"
                          />
                          <span className="text-sm text-gray-600">days after due date</span>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            onClick={() => removeReminderDay(index)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                      onClick={addReminderDay}
                    >
                      <Plus size={16} className="mr-2" />
                      Add Another Reminder
                    </button>
                  </div>
                </div>

                {/* Reminder Preview */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-3">Reminder Schedule Preview</h4>
                  <div className="text-sm text-gray-700">
                    <div className="space-y-2">
                      {formData.reminderDaysBefore > 0 && (
                        <div className="flex items-center text-blue-700">
                          <Bell size={16} className="mr-2" />
                          {formData.reminderDaysBefore} days before due date
                        </div>
                      )}
                      {formData.reminderDaysAfter.map((days, index) => (
                        <div key={index} className="flex items-center text-orange-700">
                          <Bell size={16} className="mr-2" />
                          {days} days after due date
                        </div>
                      ))}
                      {formData.reminderDaysBefore === 0 && formData.reminderDaysAfter.length === 0 && (
                        <div className="text-gray-500 italic">No reminders configured</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getActiveFeatures = () => {
    const features = [];
    if (formData.autoSendInvoices) features.push({ label: 'Auto-send Invoices', color: 'purple' });
    if (formData.autoSendQuotes) features.push({ label: 'Auto-send Quotes', color: 'purple' });
    if (formData.autoSendReminders) features.push({ label: 'Auto Reminders', color: 'orange' });
    if (formData.reminderDaysBefore > 0) features.push({ label: 'Pre-due Reminders', color: 'blue' });
    if (formData.reminderDaysAfter.length > 0) features.push({ label: 'Overdue Reminders', color: 'orange' });
    return features;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="flex items-center text-xl font-semibold text-gray-800">
              <Mail size={24} className="mr-3" />
              Email Settings
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              Configure email delivery, automation, and reminder schedules
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? 'Hide Overview' : 'Show Overview'}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Preview Section */}
        {showPreview && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h6 className="text-lg font-semibold text-gray-800 mb-4">Configuration Overview</h6>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Connection Status</h4>
                  <div className="space-y-3">
                    <div className={`p-3 rounded-lg border ${
                      formData.smtpHost 
                        ? testResult?.success 
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : testResult?.success === false
                            ? 'bg-red-50 border-red-200 text-red-700'
                            : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                    }`}>
                      <div className="flex items-center">
                        {formData.smtpHost ? (
                          testResult?.success ? (
                            <CheckCircle size={16} className="mr-2" />
                          ) : testResult?.success === false ? (
                            <AlertCircle size={16} className="mr-2" />
                          ) : (
                            <Bell size={16} className="mr-2" />
                          )
                        ) : (
                          <X size={16} className="mr-2" />
                        )}
                        <span className="text-sm font-medium">
                          {formData.smtpHost ? 
                            testResult?.success ? 'Connected & Tested' :
                            testResult?.success === false ? 'Connection Failed' :
                            'Ready to Test' : 'Not Configured'}
                        </span>
                      </div>
                    </div>
                    {formData.smtpHost && (
                      <div className="text-xs text-gray-600">
                        <div>Host: {formData.smtpHost}:{formData.smtpPort}</div>
                        <div>From: {formData.fromName} &lt;{formData.fromEmail}&gt;</div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Active Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {getActiveFeatures().map((feature, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          feature.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                          feature.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                          feature.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {feature.label}
                      </span>
                    ))}
                    {getActiveFeatures().length === 0 && (
                      <span className="text-sm text-gray-500 italic">No automation features enabled</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
          <button 
            type="button" 
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RotateCcw size={16} className="mr-2" />
            Reset to Defaults
          </button>
          <button 
            type="button"
            onClick={handleSubmit}
            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={16} className="mr-2" />
            Save Email Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;